import React, { useMemo, useState } from 'react';

// Clickable 2D plot map. Pure display: the caller supplies the layout and, per
// plot, a status group. Optionally draws over the master-plan image.
export const PLOT_STATUSES = {
  vacant: { label: 'Vacant', color: '#16a34a' },
  hold: { label: 'On hold', color: '#f59e0b' },
  booked: { label: 'Booked', color: '#dc2626' },
  sold: { label: 'Sold', color: '#6b7280' },
  reserved: { label: 'Reserved', color: '#2563eb' }
};

// Unit.status (backend enum) -> the five groups the map colours by.
const GROUP_BY_UNIT_STATUS = {
  Vacant: 'vacant',
  Available: 'vacant',
  Temporary_Booked: 'hold',
  Temporary_Booked_Incomplete: 'hold',
  Blocked: 'hold',
  Blocked_for_Broker: 'hold',
  Booked: 'booked',
  Booked_Incomplete: 'booked',
  Sold: 'sold',
  Sales_Deed_Completed: 'sold',
  Sales_Deed_with_Pending_Payments: 'sold',
  No_Dues: 'sold',
  Reserved_for_Builder: 'reserved',
  Reserved_for_Land_Owner: 'reserved',
  Reserved_for_Company: 'reserved'
};

export const statusGroupOf = (unitStatus) => GROUP_BY_UNIT_STATUS[unitStatus] || 'vacant';

const chip = (active, color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '5px 10px',
  borderRadius: 999,
  fontSize: 13,
  cursor: 'pointer',
  border: `1px solid ${color}`,
  background: active ? color : '#fff',
  color: active ? '#fff' : '#0f172a'
});

/**
 * layout: { imageWidth, imageHeight, imageUrl?, layers? { roads, green, water, hall } }
 * plots:  [{ key, label, points: [[x,y],...] (0..1), status: 'vacant'|'hold'|..., details?: [[name, value]] }]
 * onStatusChange: optional (key, status) => void. Only the demo passes it.
 */
const PlotMap = ({ layout, plots, title, subtitle, embedded = false, onStatusChange }) => {
  const hasVector = Boolean(layout.layers && Object.keys(layout.layers).length);
  const hasImage = Boolean(layout.imageUrl);
  const [mode, setMode] = useState(hasVector ? 'vector' : 'image');
  const [selectedKey, setSelectedKey] = useState(null);
  const [hoverKey, setHoverKey] = useState(null);
  const [filter, setFilter] = useState('all');

  const counts = useMemo(() => {
    const c = Object.fromEntries(Object.keys(PLOT_STATUSES).map((k) => [k, 0]));
    plots.forEach((p) => (c[p.status] += 1));
    return c;
  }, [plots]);

  const selected = plots.find((p) => p.key === selectedKey);
  const hovered = plots.find((p) => p.key === hoverKey);
  const { imageWidth: W, imageHeight: H } = layout;
  const toPoints = (pts) => pts.map(([x, y]) => `${x * W},${y * H}`).join(' ');
  const showImage = mode === 'image' && hasImage;

  return (
    <div style={{ padding: 16, fontFamily: 'system-ui, sans-serif', background: '#f8fafc', minHeight: embedded ? undefined : '100vh' }}>
      {title && <h2 style={{ margin: '0 0 4px' }}>{title}</h2>}
      {subtitle && <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: 14 }}>{subtitle}</p>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        {hasVector && hasImage && (
          <>
            <button onClick={() => setMode('vector')} style={chip(mode === 'vector', '#0f172a')}>Vector map</button>
            <button onClick={() => setMode('image')} style={chip(mode === 'image', '#0f172a')}>Image overlay</button>
            <span style={{ width: 1, height: 20, background: '#cbd5e1', margin: '0 4px' }} />
          </>
        )}
        <button onClick={() => setFilter('all')} style={chip(filter === 'all', '#0f172a')}>
          All ({plots.length})
        </button>
        {Object.entries(PLOT_STATUSES).map(([key, s]) => (
          <button key={key} onClick={() => setFilter(key)} style={chip(filter === key, s.color)}>
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: s.color, marginRight: 6 }} />
            {s.label} ({counts[key]})
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 720px', overflow: 'hidden', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            {showImage ? (
              <img src={layout.imageUrl} alt="Master plan" draggable={false} style={{ width: '100%', display: 'block', userSelect: 'none' }} />
            ) : (
              <div style={{ width: '100%', aspectRatio: `${W} / ${H}`, background: '#f1f5f9' }} />
            )}
            <svg viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
              {!showImage && hasVector && (
                <g transform={`scale(${W} ${H})`}>
                  {layout.layers.green && <path d={layout.layers.green} fill="#b9d08a" fillRule="evenodd" />}
                  {layout.layers.roads && <path d={layout.layers.roads} fill="#8b929c" fillRule="evenodd" />}
                  {layout.layers.water && <path d={layout.layers.water} fill="#7cc4e0" />}
                  {layout.layers.hall && <path d={layout.layers.hall} fill="#e8c79a" />}
                </g>
              )}
              {plots.map((p) => {
                const dim = filter !== 'all' && filter !== p.status;
                const active = p.key === selectedKey || p.key === hoverKey;
                return (
                  <polygon
                    key={p.key}
                    points={toPoints(p.points)}
                    fill={PLOT_STATUSES[p.status].color}
                    fillOpacity={dim ? 0.12 : active ? 0.95 : showImage ? 0.5 : 0.82}
                    stroke={p.key === selectedKey ? '#000' : '#fff'}
                    strokeWidth={p.key === selectedKey ? 7 : 3}
                    style={{ cursor: 'pointer', transition: 'fill-opacity .15s' }}
                    onMouseEnter={() => setHoverKey(p.key)}
                    onMouseLeave={() => setHoverKey(null)}
                    onClick={() => setSelectedKey(p.key)}
                  />
                );
              })}
              {!showImage &&
                plots.map((p) => {
                  const cx = (p.points.reduce((a, q) => a + q[0], 0) / p.points.length) * W;
                  const cy = (p.points.reduce((a, q) => a + q[1], 0) / p.points.length) * H;
                  return (
                    <text key={p.key} x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize="30" fontWeight="600" fill="#fff" style={{ pointerEvents: 'none' }}>
                      {p.label}
                    </text>
                  );
                })}
            </svg>
          </div>
          {hovered && (
            <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(15,23,42,.9)', color: '#fff', padding: '6px 10px', borderRadius: 6, fontSize: 13, pointerEvents: 'none' }}>
              <b>{hovered.label}</b> · {PLOT_STATUSES[hovered.status].label}
            </div>
          )}
        </div>

        <div style={{ flex: '0 0 280px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
          {selected ? (
            <>
              <h3 style={{ margin: '0 0 8px' }}>Plot {selected.label}</h3>
              <div style={{ fontSize: 14, lineHeight: 1.8 }}>
                {(selected.details || []).map(([name, value]) => (
                  <div key={name}>
                    {name}: <b>{value}</b>
                  </div>
                ))}
                <div>
                  Status: <b style={{ color: PLOT_STATUSES[selected.status].color }}>{PLOT_STATUSES[selected.status].label}</b>
                </div>
              </div>
              {onStatusChange && (
                <>
                  <p style={{ fontSize: 12, color: '#64748b', margin: '12px 0 6px' }}>Change status (demo):</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {Object.entries(PLOT_STATUSES).map(([key, s]) => (
                      <button key={key} onClick={() => onStatusChange(selected.key, key)} style={chip(selected.status === key, s.color)}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>Click a plot to see its details.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlotMap;
